import type { GridApi } from 'ag-grid-enterprise';

const SETTLE_DELAY_MS = 150; // wait for data to be in the DOM

/**
 * AG Grid sizes columns to content by measuring the cells it has already rendered.
 * Restoring the default column state also restores the default sort, which makes
 * the server-side row model refetch, and until the rows come back the grid holds
 * placeholder rows without data, which have nothing to measure. Size the columns
 * once the rows are there, then stop listening.
 */
export const autoSizeAllColumnsWhenRowsSettle = (api: GridApi): void => {
  let settleTimer: ReturnType<typeof setTimeout> | undefined;

  const stopWaitingForRows = (): void => {
    clearTimeout(settleTimer);
    if (!api.isDestroyed()) {
      api.removeEventListener('modelUpdated', scheduleAutoSize);
    }
  };

  const autoSizeAllColumnsWhenRowsAreLoaded = (): void => {
    if (api.isDestroyed() || api.getDisplayedRowCount() === 0) {
      stopWaitingForRows();
      return;
    }
    if (api.getDisplayedRowAtIndex(0)?.data === undefined) return;

    stopWaitingForRows();
    api.autoSizeAllColumns();
  };

  const scheduleAutoSize = (): void => {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(
      autoSizeAllColumnsWhenRowsAreLoaded,
      SETTLE_DELAY_MS
    );
  };

  api.addEventListener('modelUpdated', scheduleAutoSize);
  scheduleAutoSize();
};
